import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import usePullToRefresh from '../hooks/usePullToRefresh';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import PhotoGalleryModal from '../components/PhotoGalleryModal';
import { PRODUCT_TYPE } from '../config/product';
import { formatCurrency } from '../utils/formatCurrency';

function ServiceHistory() {
  const navigate = useNavigate();
  const { logout, isAdmin, isDriver, isSuperAdmin, shouldHidePrices } = useAuth();
  const { getAppName } = useConfig();

  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [photoCountsMap, setPhotoCountsMap] = useState({});
  const [photoGalleryStopId, setPhotoGalleryStopId] = useState(null);
  const [paymentModal, setPaymentModal] = useState({ open: false, service: null });
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: 'cash',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentNotes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customerId: '',
    routeId: '',
    paymentStatus: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.customerId) params.append('customerId', filters.customerId);
      if (filters.routeId) params.append('routeId', filters.routeId);
      if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);

      const [servicesRes, customersRes, routesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/services?${params.toString()}&product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/customers?product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/routes?product=${PRODUCT_TYPE}`),
      ]);

      setServices(servicesRes.data);
      setCustomers(customersRes.data);
      setRoutes(routesRes.data);

      // Fetch photo counts for services that have route_stop_id
      const stopIds = servicesRes.data
        .filter(s => s.route_stop_id)
        .map(s => s.route_stop_id);
      if (stopIds.length > 0) {
        try {
          const countsRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/route-stops/photo-counts`,
            { stopIds }
          );
          setPhotoCountsMap(countsRes.data);
        } catch (photoErr) {
          console.error('Failed to load photo counts:', photoErr);
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const { PullToRefreshIndicator } = usePullToRefresh(loadData);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      customerId: '',
      routeId: '',
      paymentStatus: '',
    });
  };

  // Pagination for services list
  const {
    paginatedData: paginatedServices,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalItems
  } = usePagination(services, 'serviceHistory', 25);

  // Note: service_date is pre-formatted by backend as "Mon DD, YYYY"
  // so we display it directly without Date parsing

  const formatPaymentStatus = (status, method) => {
    if (status === 'paid' && method) {
      const methodLabel = method === 'square' ? 'Square' : method === 'venmo' ? 'Venmo' : method === 'cashapp' ? 'Cash App' : 'Cash';
      return `Paid - ${methodLabel}`;
    }
    if (status === 'invoice_sent') {
      return 'Invoice Sent';
    }
    return 'Not Paid';
  };

  const getPaymentBadgeClass = (status) => {
    if (status === 'paid') return 'paid';
    if (status === 'invoice_sent') return 'invoice-sent';
    return 'not-paid';
  };

  // Open payment modal for a service
  const openPaymentModal = (service) => {
    setPaymentForm({
      paymentMethod: 'cash',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentNotes: ''
    });
    setPaymentModal({ open: true, service });
  };

  // Submit payment update
  const submitPayment = async () => {
    if (!paymentModal.service) return;

    setSavingPayment(true);
    try {
      // Use route-stops endpoint if route_stop_id exists, otherwise use services endpoint
      const endpoint = paymentModal.service.route_stop_id
        ? `${process.env.REACT_APP_API_URL}/api/route-stops/${paymentModal.service.route_stop_id}/payment`
        : `${process.env.REACT_APP_API_URL}/api/services/${paymentModal.service.id}/payment`;

      await axios.put(endpoint, {
        paymentStatus: 'paid',
        paymentMethod: paymentForm.paymentMethod,
        paymentDate: paymentForm.paymentDate,
        paymentNotes: paymentForm.paymentNotes || undefined
      });
      setPaymentModal({ open: false, service: null });
      loadData();
    } catch (err) {
      console.error('Failed to update payment:', err);
      alert('Failed to update payment status');
    } finally {
      setSavingPayment(false);
    }
  };

  // Calculate totals
  const totalServices = services.length;
  const totalRevenue = services.reduce((sum, s) => sum + parseFloat(s.price || 0), 0);
  const paidCount = services.filter(s => s.payment_status === 'paid').length;

  return (
    <div className="service-history-page">
      {/* Header */}
      <header className="app-header">
        <div className="app-header-content">
          <span className="app-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            {getAppName()}
          </span>
          <nav className="header-nav desktop-nav">
            <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => navigate('/customers')} className="nav-link">Customers</button>}
            <button onClick={() => navigate('/routes')} className="nav-link">Routes</button>
            <span className="nav-link active">History</span>
            {isAdmin() && <button onClick={() => navigate('/expenses')} className="nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => navigate('/reports')} className="nav-link">Reports</button>}
            <button onClick={() => navigate('/service-mode')} className="nav-link storm-link">Service Mode</button>
            <button onClick={() => navigate('/settings')} className="nav-link">Settings</button>
            <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>
            {isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="mobile-nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => { navigate('/customers'); setMobileMenuOpen(false); }} className="mobile-nav-link">Customers</button>}
            <button onClick={() => { navigate('/routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">Routes</button>
            <span className="mobile-nav-link active">History</span>
            {isAdmin() && <button onClick={() => { navigate('/expenses'); setMobileMenuOpen(false); }} className="mobile-nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="mobile-nav-link">Reports</button>}
            <button onClick={() => { navigate('/service-mode'); setMobileMenuOpen(false); }} className="mobile-nav-link storm-link">Service Mode</button>
            <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-link">Settings</button>
            <button onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }} className="mobile-nav-link">Help</button>
            {isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <PullToRefreshIndicator />

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">Service History</h1>
          <p className="text-muted">{totalServices} services completed</p>
        </div>

        {/* Summary Stats */}
        <div className="history-stats">
          <div className="history-stat">
            <span className="stat-value">{totalServices}</span>
            <span className="stat-label">Services</span>
          </div>
          {!shouldHidePrices() && (
            <>
              <div className="history-stat">
                <span className="stat-value">${formatCurrency(totalRevenue)}</span>
                <span className="stat-label">Revenue</span>
              </div>
              <div className="history-stat">
                <span className="stat-value">{paidCount}</span>
                <span className="stat-label">Paid</span>
              </div>
              <div className="history-stat">
                <span className="stat-value">{totalServices - paidCount}</span>
                <span className="stat-label">Unpaid</span>
              </div>
            </>
          )}
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group">
              <label>From Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="form-input"
              />
            </div>
            <div className="filter-group">
              <label>To Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="form-input"
              />
            </div>
            <div className="filter-group">
              <label>Customer</label>
              <select
                name="customerId"
                value={filters.customerId}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">All Customers</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Route</label>
              <select
                name="routeId"
                value={filters.routeId}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">All Routes</option>
                {routes.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Payment</label>
              <select
                name="paymentStatus"
                value={filters.paymentStatus}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="not_paid">Not Paid</option>
              </select>
            </div>
            <div className="filter-group filter-actions">
              <button className="btn btn-outline btn-sm" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Services List */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : services.length === 0 ? (
          <div className="empty-state">
            <p>No services found</p>
            {(filters.startDate || filters.endDate || filters.customerId || filters.routeId || filters.paymentStatus) && (
              <button className="btn btn-outline" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="services-table-container desktop-only">
              <table className="services-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Address</th>
                    <th>Route</th>
                    <th>Services</th>
                    {!shouldHidePrices() && <th className="text-right">Price</th>}
                    {!shouldHidePrices() && <th>Payment</th>}
                  </tr>
                </thead>
                <tbody>
                  {paginatedServices.map(service => (
                    <tr key={service.id}>
                      <td>{service.service_date}</td>
                      <td className="customer-cell">
                        {service.first_name} {service.last_name}
                      </td>
                      <td className="address-cell">
                        {service.address}
                        {service.city && <span className="city-text">, {service.city}</span>}
                      </td>
                      <td>{service.route_name || '-'}</td>
                      <td className="services-cell">
                        <div className="service-badges">
                          {service.service_mow && <span className="service-badge badge-mow" title="Mow">M</span>}
                          {service.service_edge && <span className="service-badge badge-edge" title="Edge">E</span>}
                          {service.service_trim && <span className="service-badge badge-trim" title="Trim">T</span>}
                          {service.service_blow && <span className="service-badge badge-blow" title="Blow">B</span>}
                          {service.service_fertilizer && <span className="service-badge badge-fert" title="Fertilizer">F</span>}
                          {service.addons && service.addons.length > 0 && service.addons.map(addon => (
                            <span
                              key={addon.addon_id}
                              className="service-badge badge-addon"
                              title={`${addon.name} (+$${parseFloat(addon.price_charged).toFixed(2)})`}
                            >
                              {addon.name.substring(0, 3).toUpperCase()}
                            </span>
                          ))}
                          {!service.service_mow && !service.service_edge && !service.service_trim && !service.service_blow && !service.service_fertilizer && (!service.addons || service.addons.length === 0) && '-'}
                        </div>
                      </td>
                      {!shouldHidePrices() && (
                        <td className={`text-right price-cell ${service.payment_status === 'paid' || service.is_comp ? 'paid' : 'unpaid'}`}>
                          ${formatCurrency(service.price)}
                        </td>
                      )}
                      {!shouldHidePrices() && (
                        <td>
                          <div className="payment-cell">
                            <span className={`payment-badge ${getPaymentBadgeClass(service.payment_status)}`}>
                              {formatPaymentStatus(service.payment_status, service.payment_method)}
                            </span>
                            {service.payment_status !== 'paid' && !service.is_comp && (
                              <button
                                className="btn-mark-paid"
                                onClick={() => openPaymentModal(service)}
                              >
                                Mark Paid
                              </button>
                            )}
                            {service.is_comp && (
                              <span className="comp-badge" title={service.comp_reason || 'Comp/Free'}>
                                COMP
                              </span>
                            )}
                            {photoCountsMap[service.route_stop_id] > 0 && (
                              <span
                                className="photo-count-badge clickable"
                                title={`${photoCountsMap[service.route_stop_id]} photo${photoCountsMap[service.route_stop_id] > 1 ? 's' : ''} - Click to view`}
                                onClick={() => setPhotoGalleryStopId(service.route_stop_id)}
                              >
                                📷 {photoCountsMap[service.route_stop_id]}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="services-cards mobile-only">
              {paginatedServices.map(service => (
                <div key={service.id} className="service-card">
                  <div className="service-card-header">
                    <span className="service-date">{service.service_date}</span>
                    {!shouldHidePrices() && (
                      <span className={`payment-badge ${getPaymentBadgeClass(service.payment_status)}`}>
                        {formatPaymentStatus(service.payment_status, service.payment_method)}
                      </span>
                    )}
                    {service.is_comp && (
                      <span className="comp-badge">COMP</span>
                    )}
                    {photoCountsMap[service.route_stop_id] > 0 && (
                      <span
                        className="photo-count-badge clickable"
                        onClick={() => setPhotoGalleryStopId(service.route_stop_id)}
                      >
                        📷 {photoCountsMap[service.route_stop_id]}
                      </span>
                    )}
                  </div>
                  <div className="service-card-body">
                    <div className="service-customer">
                      {service.first_name} {service.last_name}
                    </div>
                    <div className="service-address">
                      {service.address}{service.city && `, ${service.city}`}
                    </div>
                    {service.route_name && (
                      <div className="service-route">Route: {service.route_name}</div>
                    )}
                    {(service.service_mow || service.service_edge || service.service_trim || service.service_blow || service.service_fertilizer || (service.addons && service.addons.length > 0)) && (
                      <div className="service-badges-mobile">
                        {service.service_mow && <span className="service-badge badge-mow" title="Mow">Mow</span>}
                        {service.service_edge && <span className="service-badge badge-edge" title="Edge">Edge</span>}
                        {service.service_trim && <span className="service-badge badge-trim" title="Trim">Trim</span>}
                        {service.service_blow && <span className="service-badge badge-blow" title="Blow">Blow</span>}
                        {service.service_fertilizer && <span className="service-badge badge-fert" title="Fertilizer">Fert</span>}
                        {service.addons && service.addons.map(addon => (
                          <span
                            key={addon.addon_id}
                            className="service-badge badge-addon"
                            title={`${addon.name} (+$${parseFloat(addon.price_charged).toFixed(2)})`}
                          >
                            {addon.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="service-card-footer">
                    {!shouldHidePrices() && (
                      <span className={`service-price ${service.payment_status === 'paid' || service.is_comp ? 'paid' : 'unpaid'}`}>${formatCurrency(service.price)}</span>
                    )}
                    {!shouldHidePrices() && service.payment_status !== 'paid' && !service.is_comp && (
                      <button
                        className="btn-mark-paid"
                        onClick={() => openPaymentModal(service)}
                      >
                        Mark Paid
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      {/* Payment Modal */}
      {paymentModal.open && paymentModal.service && (
        <div className="payment-modal-overlay" onClick={() => setPaymentModal({ open: false, service: null })}>
          <div className="payment-modal" onClick={e => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>Mark as Paid</h3>
              <button className="modal-close" onClick={() => setPaymentModal({ open: false, service: null })}>&times;</button>
            </div>
            <div className="payment-modal-body">
              <p className="payment-customer">
                <strong>{paymentModal.service.first_name} {paymentModal.service.last_name}</strong>
                <br />
                <span className="payment-service-date">{paymentModal.service.service_date}</span>
                {!shouldHidePrices() && (
                  <span className="payment-amount"> - ${parseFloat(paymentModal.service.price || 0).toFixed(2)}</span>
                )}
              </p>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">Cash App</option>
                  <option value="zelle">Zelle</option>
                  <option value="square">Square</option>
                </select>
              </div>

              <div className="form-group">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <input
                  type="text"
                  value={paymentForm.paymentNotes}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentNotes: e.target.value }))}
                  placeholder="e.g., Check #1234"
                />
              </div>
            </div>
            <div className="payment-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setPaymentModal({ open: false, service: null })}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={submitPayment}
                disabled={savingPayment}
              >
                {savingPayment ? 'Saving...' : 'Mark Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }

        /* Mobile Menu */
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }

        /* Summary Stats */
        .history-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }
        .history-stat {
          background: var(--color-card);
          padding: 16px;
          border-radius: 10px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .history-stat .stat-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-primary);
        }
        .history-stat .stat-label {
          font-size: 0.75rem;
          color: var(--color-text-light);
          text-transform: uppercase;
        }

        /* Filters */
        .filters-section {
          background: var(--color-card);
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .filters-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 12px;
          align-items: end;
        }
        .filter-group label {
          display: block;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-light);
          margin-bottom: 4px;
          text-transform: uppercase;
        }
        .filter-group .form-input {
          width: 100%;
          font-size: 0.875rem;
        }
        .filter-actions {
          display: flex;
          align-items: flex-end;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 48px 16px;
          color: var(--color-text-light);
        }
        .empty-state .btn {
          margin-top: 16px;
        }

        /* Services Table (Desktop) */
        .services-table-container {
          background: var(--color-card);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .services-table {
          width: 100%;
          border-collapse: collapse;
        }
        .services-table th,
        .services-table td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--color-border);
        }
        .services-table th {
          background: var(--color-background);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--color-text-light);
        }
        .services-table tbody tr:hover {
          background: var(--color-background);
        }
        .services-table tbody tr:last-child td {
          border-bottom: none;
        }
        .customer-cell {
          font-weight: 500;
        }
        .address-cell {
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .city-text {
          color: var(--color-text-light);
        }
        .price-cell {
          font-weight: 600;
        }
        .price-cell.paid {
          color: #16a34a;
        }
        .price-cell.unpaid {
          color: #dc2626;
        }
        .text-right {
          text-align: right;
        }

        /* Payment Badge */
        .payment-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .payment-badge {
          display: inline-block;
          font-size: 0.7rem;
          padding: 4px 8px;
          border-radius: 10px;
          font-weight: 600;
          white-space: nowrap;
        }
        .payment-badge.paid {
          background: rgba(34, 197, 94, 0.15);
          color: #15803d;
        }
        .payment-badge.invoice-sent {
          background: rgba(234, 179, 8, 0.15);
          color: #a16207;
        }
        .payment-badge.not-paid {
          background: rgba(156, 163, 175, 0.15);
          color: #6b7280;
        }
        .btn-mark-paid {
          padding: 4px 10px;
          font-size: 0.7rem;
          background: #22c55e;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          white-space: nowrap;
        }
        .btn-mark-paid:hover {
          background: #16a34a;
        }

        /* Payment Modal */
        .payment-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          padding: 20px;
        }
        .payment-modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 400px;
          overflow: hidden;
        }
        .payment-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        .payment-modal-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          line-height: 1;
        }
        .payment-modal-body {
          padding: 20px;
        }
        .payment-customer {
          margin: 0 0 16px 0;
          font-size: 0.95rem;
        }
        .payment-service-date {
          color: #64748b;
        }
        .payment-amount {
          color: #059669;
          font-weight: 600;
        }
        .payment-modal .form-group {
          margin-bottom: 16px;
        }
        .payment-modal .form-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 6px;
          color: #374151;
        }
        .payment-modal .form-group select,
        .payment-modal .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.95rem;
        }
        .payment-modal-footer {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .payment-modal-footer .btn-cancel {
          flex: 1;
          padding: 10px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 8px;
          font-size: 0.95rem;
          cursor: pointer;
        }
        .payment-modal-footer .btn-confirm {
          flex: 1;
          padding: 10px;
          border: none;
          background: #22c55e;
          color: white;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
        }
        .payment-modal-footer .btn-confirm:hover {
          background: #16a34a;
        }
        .payment-modal-footer .btn-confirm:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Photo Count Badge */
        .photo-count-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 500;
          white-space: nowrap;
          background: rgba(59, 130, 246, 0.15);
          color: #2563eb;
        }
        .photo-count-badge.clickable {
          cursor: pointer;
          transition: background 0.15s, transform 0.1s;
        }
        .photo-count-badge.clickable:hover {
          background: rgba(59, 130, 246, 0.25);
          transform: scale(1.05);
        }

        /* Mobile Cards */
        .services-cards {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .service-card {
          background: var(--color-card);
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .service-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .service-date {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .service-card-body {
          margin-bottom: 12px;
        }
        .service-customer {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .service-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .service-route {
          font-size: 0.75rem;
          color: var(--color-text-light);
          margin-top: 8px;
        }
        .service-card-footer {
          display: flex;
          justify-content: flex-end;
          padding-top: 12px;
          border-top: 1px solid var(--color-border);
        }
        .service-price {
          font-size: 1.25rem;
          font-weight: 700;
        }
        .service-price.paid {
          color: #16a34a;
        }
        .service-price.unpaid {
          color: #dc2626;
        }

        /* Responsive */
        .desktop-only {
          display: block;
        }
        .mobile-only {
          display: none;
        }

        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
        }

        @media (max-width: 768px) {
          .history-stats {
            grid-template-columns: repeat(2, 1fr);
          }
          .filters-grid {
            grid-template-columns: 1fr 1fr;
          }
          .filter-actions {
            grid-column: span 2;
            justify-content: center;
          }
          .desktop-only {
            display: none;
          }
          .mobile-only {
            display: block;
          }
        }

        @media (max-width: 480px) {
          .filters-grid {
            grid-template-columns: 1fr;
          }
          .filter-actions {
            grid-column: span 1;
          }
        }

        /* Service Badges */
        .services-cell {
          white-space: nowrap;
        }
        .service-badges {
          display: flex;
          gap: 3px;
          flex-wrap: nowrap;
        }
        .service-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 4px;
          font-size: 0.7rem;
          font-weight: 600;
          border-radius: 4px;
          cursor: default;
        }
        .badge-mow {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #86efac;
        }
        .badge-edge {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #93c5fd;
        }
        .badge-trim {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fcd34d;
        }
        .badge-blow {
          background: #f3e8ff;
          color: #7c3aed;
          border: 1px solid #c4b5fd;
        }
        .badge-fert {
          background: #fce7f3;
          color: #be185d;
          border: 1px solid #f9a8d4;
        }
        .badge-addon {
          background: #f5f3ff;
          color: #5b21b6;
          border: 1px solid #ddd6fe;
        }
        .service-badges-mobile {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          margin-top: 8px;
        }
        .service-badges-mobile .service-badge {
          padding: 2px 8px;
          font-size: 0.75rem;
        }
      `}</style>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        stopId={photoGalleryStopId}
        isOpen={photoGalleryStopId !== null}
        onClose={() => setPhotoGalleryStopId(null)}
        allowDelete={isAdmin}
        title="Service Photos"
      />
    </div>
  );
}

export default ServiceHistory;

